import { safetyPercentage } from "./helpers"

// Mirrors the Android Dashboard's Watchlist: the pinned members across all of
// the user's communities, one row each.

const orderOf = (member) =>
    member.order !== undefined && member.order !== null
        ? member.order
        : Number.MAX_SAFE_INTEGER

/**
 * Pinned people and places across every community, in community order (and
 * each community's own member order), without repeats: the same person or
 * place pinned in two communities is one chaperee row per community, so this
 * dedupes by id like the Android app's getPinnedMembers.
 */
export function pinnedWatchlistMembers(communities) {
    const seen = new Set()
    const pinned = []

    for (const community of communities || []) {
        const members = [
            ...(community.persons || []),
            ...(community.places || []),
        ].sort((a, b) => orderOf(a) - orderOf(b) || a.id - b.id)

        for (const member of members) {
            if (!member.pinned || seen.has(member.id)) continue
            seen.add(member.id)
            pinned.push(member)
        }
    }

    return pinned
}

export const initialsOf = (name) =>
    (name || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0].toUpperCase())
        .join("") || "?"

/**
 * What one watchlist row displays.
 *
 * A person's second line is where they are (flag plus place name) once a
 * location has resolved, otherwise why there isn't one. A place's is its
 * country. `score` is a 0-100 percentage (null when unscored) for the ring
 * colour: for a person their own score, for a place the place's.
 */
export function watchlistRow(member) {
    const isPerson = member.type === "person"

    const name = isPerson
        ? member.full_name ||
          member.username ||
          member.name ||
          member.email ||
          "Unknown"
        : member.fromGoogle?.name || member.name || "Unknown"

    let detail = ""
    let detailCountryCode = null
    if (isPerson) {
        if (member.entity_name) {
            detail = member.entity_name
            detailCountryCode = member.country_code || null
        } else if (member.status === "invited") {
            detail = "Invited"
        } else if (member.latitude == null || member.longitude == null) {
            detail = "No location data"
        } else {
            detail = "No data available yet"
        }
    } else {
        detail = member.country || ""
    }

    return {
        id: member.id,
        type: isPerson ? "person" : "place",
        name,
        initials: initialsOf(name),
        detail,
        detailCountryCode,
        countryCode: member.country_code || null,
        pictureUrl: member.profile_picture_url || null,
        score:
            member.overall_safety == null
                ? null
                : Number(safetyPercentage(member.overall_safety)),
        slug: member.slug || null,
    }
}
